import type { Filter, Query } from '@cubejs-client/core'

import { buildCubeQuery } from '@/lib/cube'
import { bindFilter, shortName, type ReportField, type ReportFilterDef } from '@/lib/cube-report'
import { periodRange, type PeriodFilterDef } from '@/lib/period'
import type { ReportDocument } from '@/lib/report-document'
import { buildDefinedFilters, type ReportFilterState } from '@/services/cube/report'

/*
 * Belge şablonunun filtre ve adlandırılmış sorgu kurucuları. Saf fonksiyonlar: canlı önizleme de
 * dönem kopyası üretimi (D7) de aynı Cube sorgusunu kurar (docs/REPORT-DOCUMENTS-PLAN.md §6).
 */

export type DocumentFilter = ReportDocument['filters'][number]
export type DocumentQuery = ReportDocument['queries'][string]

/** Belge filtreleri -> `useReportFilters` kaynağı: üye, filtrenin kendi view'ıyla nitelenir. */
export function documentFilterDefs(
  filters: DocumentFilter[],
): (ReportFilterDef | PeriodFilterDef)[] {
  return filters.map((filter) => ({
    member: `${filter.view}.${filter.member}`,
    type: filter.type,
    ...(filter.default !== undefined && { default: filter.default }),
  })) as (ReportFilterDef | PeriodFilterDef)[]
}

/**
 * Sorgulara ve parçalara uygulanan sayfa filtreleri: `period` bir `dateRange` gibi çalışır
 * (aralığı `state.dateFrom/dateTo` taşır, `useReportFilters` `periodRange` ile doldurur).
 */
export function pageFilterDefs(defs: (ReportFilterDef | PeriodFilterDef)[]): ReportFilterDef[] {
  return defs.map((def) =>
    def.type === 'period' ? { member: def.member, type: 'dateRange' as const } : def,
  )
}

/** Filtreyi bu view'ın üyesine uyarlar (aynı kısa adlı üyesi yoksa uygulanmaz); durum anahtarı korunur. */
function filtersForView(
  view: string,
  fields: ReadonlyMap<string, ReportField>,
  pageFilters: ReportFilterDef[],
): ReportFilterDef[] {
  return pageFilters.flatMap((filter) => {
    const member = `${view}.${shortName(filter.member)}`
    return fields.has(member) ? [bindFilter(filter, member, fields.get(member))] : []
  })
}

/** Sorgunun başvurduğu ama view'da olmayan üyeler (kısa ad). */
export function missingMembers(
  query: DocumentQuery,
  fields: ReadonlyMap<string, ReportField>,
): string[] {
  return [...query.measures, ...query.dimensions].filter(
    (member) => !fields.has(`${query.view}.${member}`),
  )
}

/** Adlandırılmış sorgu -> Cube sorgusu; üyeler `view.` ile nitelenir. */
export function namedQuery(
  query: DocumentQuery,
  fields: ReadonlyMap<string, ReportField>,
  pageFilters: ReportFilterDef[],
  state: ReportFilterState,
): Query {
  const q = (short: string) => `${query.view}.${short}`
  const filters: Filter[] = buildDefinedFilters(
    filtersForView(query.view, fields, pageFilters),
    state,
  )
  for (const { member, values } of query.where) {
    filters.push({ member: q(member), operator: 'equals', values })
  }
  return buildCubeQuery({
    measures: query.measures.map(q),
    dimensions: query.dimensions.map(q),
    filters,
    order: query.order.map((token): [string, 'asc' | 'desc'] =>
      token.startsWith('-') ? [q(token.slice(1)), 'desc'] : [q(token), 'asc'],
    ),
    ...(query.limit !== undefined && { limit: query.limit }),
  })
}

/** Durumun tarih aralığını verilen dönemin (YYYY-MM) aralığıyla değiştirir (önceki dönem sorgusu). */
export function withPeriod(state: ReportFilterState, period: string): ReportFilterState {
  const [dateFrom, dateTo] = periodRange(period)
  return { ...state, dateFrom, dateTo }
}
